import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Language, Direction } from '../types';

interface LanguageContextType {
  language: Language;
  dir: Direction;
  changeLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps): React.ReactElement {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<Language>(
    (localStorage.getItem('language') as Language) || 'ar'
  );
  const [dir, setDir] = useState<Direction>(language === 'ar' ? 'rtl' : 'ltr');

  useEffect(() => {
    document.documentElement.lang = language;
    document.body.dir = dir;
    localStorage.setItem('language', language);
  }, [language, dir]);

  const changeLanguage = (lang: Language): void => {
    setLanguage(lang);
    setDir(lang === 'ar' ? 'rtl' : 'ltr');
    i18n.changeLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, dir, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
